import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Autocomplete,
  Tooltip,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  CloudUpload,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Image as ImageIcon,
  AudioFile as AudioIcon,
  VideoFile as VideoIcon,
  PictureAsPdf as PdfIcon,
  Description as WordIcon,
  CheckCircle as CheckIcon,
  Gavel as ApproveIcon,
  History as VersionsIcon,
  RemoveCircleOutline as WithdrawIcon,
  Replay as ReplayIcon,
  RestartAlt as RestartAltIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './ContentManagementPage.css';
import AssetVersionsDialog from './AssetVersionsDialog';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const ContentManagementPage = () => {
  const { getAccessToken } = useAuth();
  // State management
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadSectionOpen, setUploadSectionOpen] = useState(true);
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  // Form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    type: 'AUDIO',
    fileName: '',
    contentType: '',
    visibility: 'PAID',
    price: '',
    releaseDate: '',
    thumbnailUrl: '',
    categoryId: '',
    tagIds: [],
    bundleIds: [],
    bundleOnly: false,
    eligibleTeams: ['all']
  });
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const [editThumbnail, setEditThumbnail] = useState(null);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [editForm, setEditForm] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    type: 'ALL',
    category: 'ALL',
    status: 'ALL',
    tag: 'ALL',
    approval: 'ALL'
  });
  
  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Resume-stuck-asset state. `resumingAssetIds` tracks the per-row spinner so
  // multiple admins clicking Resume on different rows don't blow each other away.
  const [resumingAssetIds, setResumingAssetIds] = useState(new Set());
  // Which asset's file-version history is open. Null = the dialog is closed.
  const [versionsAsset, setVersionsAsset] = useState(null);

  const [bulkResumeOpen, setBulkResumeOpen] = useState(false);
  const [bulkResumeRunning, setBulkResumeRunning] = useState(false);
  const [bulkResumeForm, setBulkResumeForm] = useState({
    thresholdMinutes: 30,
    statuses: { PROCESSING: true, FAILED: true, UPLOADING: false }
  });
  const [bulkResumeResult, setBulkResumeResult] = useState(null);
  const [bulkResumeShowItems, setBulkResumeShowItems] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    fetchAssets();
    fetchCategories();
    fetchTags();
    fetchBundles();
    fetchTeams();
  }, []);

  // Fetch assets
  const fetchAssets = async () => {
    setLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      const response = await axios.get(`${API_BASE_URL}/api/v1/content/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data);
    } catch (error) {
      showNotification('Failed to fetch assets', 'error');
      console.error('Error fetching assets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/api/v1/content/categories/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch tags
  const fetchTags = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/api/v1/content/tags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  // Fetch bundles
  const fetchBundles = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/api/v1/content/bundles?activeOnly=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBundles(response.data);
    } catch (error) {
      console.error('Error fetching bundles:', error);
    }
  };

  // Fetch teams
  const fetchTeams = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/em/v1/admin/teams`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch teams');
      }

      const data = await response.json();
      if (data.status === 'success') {
        setTeams(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  // Validate file
  const validateFile = (file) => {
    if (!file) return false;
    
    // Validate file size (3GB max for media, 50MB for documents)
    const maxSize = ['PDF', 'WORD'].includes(uploadForm.type) ? 50 * 1024 * 1024 : 3 * 1024 * 1024 * 1024;
    if (file.size > maxSize) {
      showNotification(`File size must be less than ${maxSize >= 1024 * 1024 * 1024 ? (maxSize / (1024 * 1024 * 1024)) + 'GB' : (maxSize / (1024 * 1024)) + 'MB'}`, 'error');
      return false;
    }
    
    // Validate file type
    const validTypes = {
      'AUDIO': ['audio/'],
      'VIDEO': ['video/'],
      'PDF': ['application/pdf'],
      'WORD': ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    };
    
    const typeMatches = validTypes[uploadForm.type]?.some(type => file.type.includes(type));
    if (!typeMatches) {
      showNotification(`Please select a valid ${uploadForm.type} file`, 'error');
      return false;
    }
    
    return true;
  };

  const detectFileType = (file) => {
    if (file.type.startsWith('video/')) return 'VIDEO';
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) return 'PDF';
    if (file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx')) return 'WORD';
    if (file.type.startsWith('audio/')) return 'AUDIO';
    return 'AUDIO';
  };

  const getContentType = (file) => {
    if (file.type) return file.type;
    const ext = file.name.toLowerCase().split('.').pop();
    const mimeMap = {
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      mp3: 'audio/mpeg',
      mp4: 'video/mp4',
      wav: 'audio/wav',
      m4a: 'audio/mp4',
    };
    return mimeMap[ext] || 'application/octet-stream';
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const detectedType = detectFileType(file);
      const contentType = getContentType(file);
      
      setSelectedFile(file);
      setUploadForm({
        ...uploadForm,
        fileName: file.name,
        contentType,
        type: detectedType
      });
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      const detectedType = detectFileType(file);
      const contentType = getContentType(file);
      setSelectedFile(file);
      setUploadForm({
        ...uploadForm,
        fileName: file.name,
        contentType,
        type: detectedType
      });
    }
  };

  // Parse and validate price
  const parsePrice = (priceValue) => {
    if (!priceValue || priceValue === '') return null;
    const parsed = parseFloat(priceValue);
    if (isNaN(parsed) || parsed < 0) {
      throw new Error('Invalid price format');
    }
    return Math.round(parsed * 100) / 100; // Round to 2 decimal places
  };

  const uploadThumbnailFile = async (file) => {
    const token = await getAccessToken();
    if (!token) {
      throw new Error('Authentication token not available');
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'content-thumbnails');

    const response = await axios.post(
      `${API_BASE_URL}/em/v1/admin/images/upload`,
      formData,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to upload thumbnail');
    }
    return response.data.data.imageUrl;
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile) {
      showNotification('Please select a file', 'error');
      return;
    }

    if (!uploadForm.title) {
      showNotification('Please enter a title', 'error');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      let thumbnailUrl = uploadForm.thumbnailUrl || null;
      if (selectedThumbnail) {
        setThumbnailUploading(true);
        try {
          thumbnailUrl = await uploadThumbnailFile(selectedThumbnail);
        } finally {
          setThumbnailUploading(false);
        }
      }
      
      // Step 1: Initiate upload with all metadata
      const initiatePayload = {
        title: uploadForm.title,
        description: uploadForm.description,
        type: uploadForm.type,
        fileName: uploadForm.fileName,
        contentType: uploadForm.contentType,
        visibility: uploadForm.visibility,
        price: parsePrice(uploadForm.price),
        releaseDate: uploadForm.releaseDate || null,
        thumbnailUrl,
        categoryId: uploadForm.categoryId || null,
        tagIds: uploadForm.tagIds && uploadForm.tagIds.length > 0 ? uploadForm.tagIds : null,
        bundleIds: uploadForm.bundleIds && uploadForm.bundleIds.length > 0 ? uploadForm.bundleIds : null,
        bundleOnly: uploadForm.bundleOnly || false,
        eligibleTeams: uploadForm.eligibleTeams || ['all']
      };
      
      const initiateResponse = await axios.post(
        `${API_BASE_URL}/api/v1/content/assets/upload/initiate`,
        initiatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { assetId, uploadUrl } = initiateResponse.data;

      // Step 2: Upload file to S3 via presigned URL (with fallback to direct upload)
      setUploadProgress(10);
      const s3ContentType = uploadForm.contentType || getContentType(selectedFile);
      let useDirectUpload = false;

      try {
        console.log('Uploading to S3:', { contentType: s3ContentType, fileSize: selectedFile.size });
        await axios.put(uploadUrl, selectedFile, {
          headers: { 'Content-Type': s3ContentType },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(10 + (progressEvent.loaded * 80) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        });
      } catch (s3Error) {
        console.warn('S3 presigned upload failed, falling back to direct upload:', s3Error.response?.status, s3Error.message);
        useDirectUpload = true;
      }

      // Fallback: upload through the backend directly
      if (useDirectUpload) {
        console.log('Using direct upload fallback via backend');
        const directFormData = new FormData();
        directFormData.append('file', selectedFile);

        try {
          await axios.post(
            `${API_BASE_URL}/api/v1/content/assets/${assetId}/upload/direct`,
            directFormData,
            {
              headers: { Authorization: `Bearer ${token}` },
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round(10 + (progressEvent.loaded * 80) / progressEvent.total);
                setUploadProgress(percentCompleted);
              }
            }
          );
        } catch (directError) {
          console.error('Direct upload also failed:', directError.response?.status, directError.response?.data || directError.message);
          throw new Error(`File upload failed: ${directError.response?.data || directError.message}`);
        }
      }

      setUploadProgress(90);

      // Get fresh token for remaining API calls
      const freshToken = await getAccessToken();
      if (!freshToken) {
        throw new Error('Authentication token expired during upload');
      }

      // Step 3: Complete upload (only needed if presigned URL flow was used; direct upload handles this internally)
      if (!useDirectUpload) {
        try {
          await axios.post(
            `${API_BASE_URL}/api/v1/content/assets/${assetId}/upload/complete`,
            {},
            { headers: { Authorization: `Bearer ${freshToken}` } }
          );
        } catch (completeError) {
          console.error('Complete upload failed:', completeError.response?.status, completeError.response?.data || completeError.message);
          throw new Error(`Upload completion failed: ${completeError.response?.data || completeError.message}`);
        }
      }

      setUploadProgress(95);

      // Step 4: Update asset with additional metadata (price, category, tags, bundle)
      const updatePayload = {
        title: uploadForm.title,
        description: uploadForm.description,
        visibility: uploadForm.visibility,
        price: parsePrice(uploadForm.price),
        releaseDate: uploadForm.releaseDate || null,
        thumbnailUrl,
        categoryId: uploadForm.categoryId || null,
        tagIds: uploadForm.tagIds && uploadForm.tagIds.length > 0 ? uploadForm.tagIds : [],
        bundleIds: uploadForm.bundleIds && uploadForm.bundleIds.length > 0 ? uploadForm.bundleIds : [],
        eligibleTeams: uploadForm.eligibleTeams || ['all']
      };

      await axios.put(
        `${API_BASE_URL}/api/v1/content/assets/${assetId}`,
        updatePayload,
        { headers: { Authorization: `Bearer ${freshToken}` } }
      );

      setUploadProgress(100);

      const isDocument = ['PDF', 'WORD'].includes(uploadForm.type);
      showNotification(
        isDocument ? 'Document uploaded successfully!' : 'Content uploaded successfully! Transcoding in progress...',
        'success'
      );
      setUploadSectionOpen(false);
      resetUploadForm();
      fetchAssets();
    } catch (error) {
      handleApiError(error, 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle team selection
  const handleTeamSelection = (e, formType = 'upload') => {
    const { value, checked } = e.target;

    const updateTeams = (currentTeams) => {
      if (value === 'all') {
        return checked ? ['all'] : [];
      } else {
        const filteredTeams = currentTeams.filter(team => team !== 'all');
        if (checked) {
          return [...filteredTeams, value];
        } else {
          return filteredTeams.filter(team => team !== value);
        }
      }
    };

    if (formType === 'edit') {
      setEditForm(prev => ({
        ...prev,
        eligibleTeams: updateTeams(prev.eligibleTeams)
      }));
    } else {
      setUploadForm(prev => ({
        ...prev,
        eligibleTeams: updateTeams(prev.eligibleTeams)
      }));
    }
  };

  // Helper to display eligible teams
  const getEligibleTeamsDisplay = (eligibleTeams) => {
    if (!eligibleTeams || eligibleTeams.length === 0) {
      return 'No team restrictions';
    }
    if (eligibleTeams.includes('all')) {
      return 'All teams eligible';
    }
    const teamNames = eligibleTeams.map(teamId => {
      const team = teams.find(t => t.name === teamId || t.id === teamId);
      return team ? team.name : teamId;
    });
    if (teamNames.length <= 2) {
      return teamNames.join(', ');
    }
    return `${teamNames.slice(0, 2).join(', ')} +${teamNames.length - 2} more`;
  };

  // Handle edit
  const handleEdit = (asset) => {
    setEditingAssetId(asset.id);
    setEditForm({
      ...asset,
      tagIds: asset.tags ? asset.tags.map(t => t.id) : [],
      bundleIds: asset.bundles ? asset.bundles.map(b => b.id) : [],
      eligibleTeams: asset.eligibleTeams || ['all']
    });
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingAssetId(null);
    setEditForm(null);
    setEditThumbnail(null);
  };

  // Handle update
  const handleUpdate = async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      let thumbnailUrl = editForm.thumbnailUrl || null;
      if (editThumbnail) {
        setThumbnailUploading(true);
        try {
          thumbnailUrl = await uploadThumbnailFile(editThumbnail);
        } finally {
          setThumbnailUploading(false);
        }
      }

      const updatePayload = {
        title: editForm.title,
        description: editForm.description,
        visibility: editForm.visibility,
        price: parsePrice(editForm.price),
        releaseDate: editForm.releaseDate || null,
        thumbnailUrl,
        categoryId: editForm.categoryId || null,
        tagIds: editForm.tagIds && editForm.tagIds.length > 0 ? editForm.tagIds : [],
        bundleIds: editForm.bundleIds && editForm.bundleIds.length > 0 ? editForm.bundleIds : [],
        bundleOnly: editForm.bundleOnly || false,
        eligibleTeams: editForm.eligibleTeams || ['all']
      };

      console.log('Updating asset with payload:', updatePayload);

      await axios.put(
        `${API_BASE_URL}/api/v1/content/assets/${editForm.id}`,
        updatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showNotification('Content updated successfully', 'success');
      setEditingAssetId(null);
      setEditForm(null);
      setEditThumbnail(null);
      fetchAssets();
    } catch (error) {
      handleApiError(error, 'Update failed');
    }
  };

  // Handle delete
  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) {
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      await axios.delete(
        `${API_BASE_URL}/api/v1/content/assets/${assetId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showNotification('Content deleted successfully', 'success');
      fetchAssets();
    } catch (error) {
      handleApiError(error, 'Delete failed');
    }
  };

  // Resume a single stuck asset by re-publishing its transcode job.
  // The backend handles all the state transitions; we just toggle a spinner
  // for this row, surface the response action/reason, and refresh the table.
  const handleResumeAsset = async (asset) => {
    if (!asset?.id) return;
    if (!window.confirm(`Resume "${asset.title}"? This re-queues the transcode job.`)) {
      return;
    }

    setResumingAssetIds((prev) => {
      const next = new Set(prev);
      next.add(asset.id);
      return next;
    });

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/content/assets/${asset.id}/resume`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const item = response.data || {};
      const action = item.action || 'UNKNOWN';
      const reason = item.reason || '';
      const severity = action === 'SKIPPED' || action === 'FAILED' ? 'warning' : 'success';
      showNotification(
        `Resume ${action.toLowerCase()}${reason ? `: ${reason}` : ''}`,
        severity
      );
      fetchAssets();
    } catch (error) {
      handleApiError(error, 'Failed to resume asset');
    } finally {
      setResumingAssetIds((prev) => {
        const next = new Set(prev);
        next.delete(asset.id);
        return next;
      });
    }
  };

  // Bulk-resume: collect chosen statuses, hit the resume-stuck endpoint, and
  // stash the response so the dialog can render counts + per-asset details.
  const handleResumeStuck = async () => {
    const selected = Object.entries(bulkResumeForm.statuses)
      .filter(([, on]) => on)
      .map(([s]) => s);
    if (selected.length === 0) {
      showNotification('Select at least one status to resume', 'warning');
      return;
    }
    const minutes = Number(bulkResumeForm.thresholdMinutes);
    if (!Number.isFinite(minutes) || minutes < 1) {
      showNotification('Threshold (minutes) must be at least 1', 'warning');
      return;
    }

    setBulkResumeRunning(true);
    setBulkResumeResult(null);
    setBulkResumeShowItems(false);
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }
      const params = new URLSearchParams();
      params.append('thresholdMinutes', String(minutes));
      // Repeated `statuses` query params bind to a Spring `List<AssetStatus>` cleanly.
      selected.forEach((s) => params.append('statuses', s));
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/content/assets/resume-stuck?${params.toString()}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBulkResumeResult(response.data);
      const { requeuedCount = 0, markedReadyCount = 0, skippedCount = 0, failedCount = 0 } = response.data || {};
      showNotification(
        `Resumed ${requeuedCount} requeued, ${markedReadyCount} ready, ${skippedCount} skipped, ${failedCount} failed`,
        failedCount > 0 ? 'warning' : 'success'
      );
      fetchAssets();
    } catch (error) {
      handleApiError(error, 'Bulk resume failed');
    } finally {
      setBulkResumeRunning(false);
    }
  };

  const closeBulkResumeDialog = () => {
    if (bulkResumeRunning) return;
    setBulkResumeOpen(false);
    setBulkResumeResult(null);
    setBulkResumeShowItems(false);
  };

  // Reset upload form
  const resetUploadForm = () => {
    setUploadForm({
      title: '',
      description: '',
      type: 'AUDIO',
      fileName: '',
      contentType: '',
      visibility: 'PAID',
      price: '',
      releaseDate: '',
      thumbnailUrl: '',
      categoryId: '',
      tagIds: [],
      bundleIds: [],
      eligibleTeams: ['all']
    });
    setSelectedFile(null);
    setSelectedThumbnail(null);
  };

  // Show notification
  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  // Handle API errors
  const handleApiError = (error, defaultMessage) => {
    if (error.response?.status === 401) {
      showNotification('Session expired. Please login again.', 'error');
    } else if (error.response?.status === 403) {
      showNotification('You do not have permission to perform this action.', 'error');
    } else if (error.response?.data?.message) {
      showNotification(error.response.data.message, 'error');
    } else {
      showNotification(defaultMessage, 'error');
    }
  };

  // Get status color
  // Members see nothing until it is approved — including content they have already bought — so
  // this is the switch that actually publishes a file.
  const handleToggleApproval = async (asset) => {
    const approved = asset.approved !== true;
    try {
      const token = await getAccessToken();
      if (!token) { showNotification('Authentication required', 'error'); return; }
      await axios.post(
        `${API_BASE_URL}/api/v1/content/admin/approval/assets/${asset.id}?approved=${approved}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification(
        approved ? `"${asset.title}" is now visible to members` : `"${asset.title}" withdrawn from members`,
        'success'
      );
      fetchAssets();
    } catch (error) {
      handleApiError(error, 'Could not change approval');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'UPLOADING': return 'info';
      case 'PROCESSING': return 'warning';
      case 'READY': return 'success';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  // Filter assets
  const filteredAssets = assets.filter(asset => {
    if (filters.search && !asset.title.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.type !== 'ALL' && asset.type !== filters.type) {
      return false;
    }
    if (filters.approval !== 'ALL') {
      const approved = asset.approved === true;
      if (filters.approval === 'APPROVED' && !approved) return false;
      if (filters.approval === 'PENDING' && approved) return false;
    }
    if (filters.status !== 'ALL' && asset.status !== filters.status) {
      return false;
    }
    if (filters.category !== 'ALL' && asset.categoryId !== parseInt(filters.category)) {
      return false;
    }
    if (filters.tag !== 'ALL') {
      const tagId = parseInt(filters.tag);
      const hasTag = asset.tags && asset.tags.some(t => t.id === tagId);
      if (!hasTag) {
        return false;
      }
    }
    return true;
  });

  return (
    <Box className="content-management-page">
      {/* Header */}
      <Box className="page-header">
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Content Management</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<RestartAltIcon />}
            onClick={() => setBulkResumeOpen(true)}
          >
            Resume Stuck Assets
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={uploadSectionOpen ? <ExpandLessIcon /> : <AddIcon />}
            onClick={() => setUploadSectionOpen(!uploadSectionOpen)}
          >
            {uploadSectionOpen ? 'Hide Upload Form' : 'Upload Content'}
          </Button>
        </Stack>
      </Box>

      {/* Inline Upload Section */}
      <Collapse in={uploadSectionOpen}>
        <Card className="upload-section" sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <UploadIcon color="primary" />
                Upload New Content
              </Typography>
              <IconButton size="small" onClick={() => setUploadSectionOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* File Upload Zone */}
            <Box
              className={`file-upload-zone ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'file-selected' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: dragActive ? 'primary.main' : selectedFile ? 'success.main' : 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                bgcolor: dragActive ? 'primary.50' : selectedFile ? 'success.50' : 'grey.50',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                mb: 3,
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50'
                }
              }}
            >
              <input
                type="file"
                id="file-upload-input"
                hidden
                accept="audio/*,video/*,application/pdf,.pdf,application/msword,.doc,.docx"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              <label htmlFor="file-upload-input" style={{ cursor: uploading ? 'not-allowed' : 'pointer', display: 'block' }}>
                {selectedFile ? (
                  <Box>
                    {uploadForm.type === 'AUDIO' ? (
                      <AudioIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                    ) : uploadForm.type === 'VIDEO' ? (
                      <VideoIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                    ) : uploadForm.type === 'PDF' ? (
                      <PdfIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                    ) : (
                      <WordIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                    )}
                    <Typography variant="h6" color="success.main" gutterBottom>
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                    </Typography>
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedFile(null);
                        setUploadForm({ ...uploadForm, fileName: '', contentType: '' });
                      }}
                      disabled={uploading}
                    >
                      Change File
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <CloudUpload sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Drag & Drop or Click to Upload
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Supports audio/video files up to 3GB, documents up to 50MB
                    </Typography>
                  </Box>
                )}
              </label>
            </Box>

            {/* Form Sections */}
            <Box>
              {/* Section 1: Basic Information */}
              <Box className="form-section" mb={3}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Title"
                      required
                      value={uploadForm.title}
                      onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                      disabled={uploading}
                      placeholder="Enter content title"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">Type *</FormLabel>
                      <RadioGroup
                        row
                        value={uploadForm.type}
                        onChange={(e) => setUploadForm({ ...uploadForm, type: e.target.value })}
                      >
                        <FormControlLabel 
                          value="AUDIO" 
                          control={<Radio />} 
                          label="Audio" 
                          disabled={uploading || selectedFile} 
                        />
                        <FormControlLabel 
                          value="VIDEO" 
                          control={<Radio />} 
                          label="Video" 
                          disabled={uploading || selectedFile} 
                        />
                        <FormControlLabel 
                          value="PDF" 
                          control={<Radio />} 
                          label="PDF" 
                          disabled={uploading || selectedFile} 
                        />
                        <FormControlLabel 
                          value="WORD" 
                          control={<Radio />} 
                          label="Word" 
                          disabled={uploading || selectedFile} 
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={3}
                      value={uploadForm.description}
                      onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                      disabled={uploading}
                      placeholder="Enter a detailed description"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Section 2: Pricing & Access */}
              <Box className="form-section" mb={3}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Pricing & Access
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Visibility</InputLabel>
                      <Select
                        value={uploadForm.visibility}
                        onChange={(e) => setUploadForm({ ...uploadForm, visibility: e.target.value })}
                        label="Visibility"
                        disabled={uploading}
                      >
                        <MenuItem value="PUBLIC">Public (Free)</MenuItem>
                        <MenuItem value="PAID">Paid</MenuItem>
                        <MenuItem value="SUBSCRIBERS_ONLY">Subscribers Only</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Price"
                      type="number"
                      value={uploadForm.price}
                      onChange={(e) => setUploadForm({ ...uploadForm, price: e.target.value })}
                      disabled={uploading || uploadForm.visibility === 'PUBLIC'}
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MoneyIcon />
                          </InputAdornment>
                        )
                      }}
                      helperText={uploadForm.visibility === 'PUBLIC' ? 'Public content is free' : 'Enter price in USD'}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Section 3: Organization */}
              <Box className="form-section" mb={3}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Organization
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={{ minWidth: 200 }}>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={uploadForm.categoryId}
                        onChange={(e) => setUploadForm({ ...uploadForm, categoryId: e.target.value })}
                        label="Category"
                        disabled={uploading}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {categories.map(cat => (
                          <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      options={bundles}
                      getOptionLabel={(b) => b.title}
                      value={bundles.filter(b => (uploadForm.bundleIds || []).includes(b.id))}
                      onChange={(e, newValue) => {
                        setUploadForm({ ...uploadForm, bundleIds: newValue.map(b => b.id) });
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((opt, i) => (
                          <Chip
                            key={opt.id}
                            label={opt.title}
                            size="small"
                            {...getTagProps({ index: i })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Bundles"
                          placeholder="Select bundles"
                          sx={{ minWidth: 200 }}
                        />
                      )}
                      disabled={uploading}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={tags}
                      getOptionLabel={(option) => option.name}
                      value={tags.filter(t => uploadForm.tagIds.includes(t.id))}
                      onChange={(e, newValue) => {
                        setUploadForm({ ...uploadForm, tagIds: newValue.map(v => v.id) });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Tags" placeholder="Select tags" sx={{ minWidth: 200 }} />
                      )}
                      disabled={uploading}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={uploadForm.bundleOnly || false}
                          onChange={(e) => setUploadForm({ ...uploadForm, bundleOnly: e.target.checked })}
                          disabled={uploading}
                        />
                      }
                      label="Bundle Only"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, ml: 4 }}>
                      When enabled, this content will only be available through bundles and won't appear in the public catalog.
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Section: Eligible Teams */}
              <Box className="form-section" mb={3}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Eligible Teams
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        value="all"
                        checked={uploadForm.eligibleTeams.includes('all')}
                        onChange={(e) => handleTeamSelection(e, 'upload')}
                        disabled={uploading}
                      />
                    }
                    label="All Teams"
                  />
                  {teams.map(team => (
                    <FormControlLabel
                      key={team.id || team.name}
                      control={
                        <Checkbox
                          value={team.name}
                          checked={uploadForm.eligibleTeams.includes(team.name)}
                          onChange={(e) => handleTeamSelection(e, 'upload')}
                          disabled={uploading || uploadForm.eligibleTeams.includes('all')}
                        />
                      }
                      label={team.name}
                    />
                  ))}
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Section 4: Media & Release */}
              <Box className="form-section" mb={3}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Media & Release
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Release Date"
                      type="date"
                      value={uploadForm.releaseDate}
                      onChange={(e) => setUploadForm({ ...uploadForm, releaseDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={uploading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Thumbnail Image (optional)
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<ImageIcon />}
                        disabled={uploading || thumbnailUploading}
                        fullWidth
                        sx={{ mb: 1, justifyContent: 'flex-start', textTransform: 'none' }}
                      >
                        {selectedThumbnail ? selectedThumbnail.name : 'Choose File'}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setSelectedThumbnail(file);
                              setUploadForm(prev => ({ ...prev, thumbnailUrl: '' }));
                            }
                          }}
                        />
                      </Button>
                      {selectedThumbnail && (
                        <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
                          <img
                            src={URL.createObjectURL(selectedThumbnail)}
                            alt="Thumbnail preview"
                            style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 8, objectFit: 'cover' }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => setSelectedThumbnail(null)}
                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'error.light', color: 'white' } }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                      {!selectedThumbnail && (
                        <TextField
                          fullWidth
                          size="small"
                          label="Or paste URL"
                          value={uploadForm.thumbnailUrl}
                          onChange={(e) => setUploadForm({ ...uploadForm, thumbnailUrl: e.target.value })}
                          placeholder="https://example.com/thumbnail.jpg"
                          disabled={uploading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <ImageIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Upload Progress */}
              {(uploading || thumbnailUploading) && (
                <Box mb={3}>
                  <LinearProgress
                    variant={thumbnailUploading && !uploading ? 'indeterminate' : 'determinate'}
                    value={uploadProgress}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                  <Typography variant="caption" align="center" display="block" mt={1}>
                    {thumbnailUploading && !uploading ? 'Uploading thumbnail...' : `Uploading... ${uploadProgress}%`}
                  </Typography>
                </Box>
              )}

              {/* Action Buttons */}
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    resetUploadForm();
                    setUploadSectionOpen(false);
                  }}
                  disabled={uploading}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<UploadIcon />}
                  onClick={handleUpload}
                  disabled={uploading || !selectedFile || !uploadForm.title}
                >
                  {uploading ? 'Uploading...' : 'Upload Content'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Collapse>

      {/* Filters */}
      <Paper className="filters-section">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search content..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: <SearchIcon />
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                label="Type"
              >
                <MenuItem value="ALL">All Types</MenuItem>
                <MenuItem value="AUDIO">Audio</MenuItem>
                <MenuItem value="VIDEO">Video</MenuItem>
                <MenuItem value="PDF">PDF</MenuItem>
                <MenuItem value="WORD">Word</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                label="Category"
              >
                <MenuItem value="ALL">All Categories</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="ALL">All Status</MenuItem>
                <MenuItem value="UPLOADING">Uploading</MenuItem>
                <MenuItem value="PROCESSING">Processing</MenuItem>
                <MenuItem value="READY">Ready</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Approval</InputLabel>
              <Select
                value={filters.approval}
                onChange={(e) => setFilters({ ...filters, approval: e.target.value })}
                label="Approval"
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="PENDING">Pending approval</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Tag</InputLabel>
              <Select
                value={filters.tag}
                onChange={(e) => setFilters({ ...filters, tag: e.target.value })}
                label="Tag"
              >
                <MenuItem value="ALL">All Tags</MenuItem>
                {tags.map(tag => (
                  <MenuItem key={tag.id} value={tag.id}>{tag.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Content Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Content Library
          </Typography>
          <TableContainer>
            {loading ? (
              <Box p={3}><LinearProgress /></Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Content</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tags</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Bundles</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Teams</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Release Date</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAssets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <Box py={4}>
                          <Typography color="textSecondary" variant="h6">No content found</Typography>
                          <Typography color="textSecondary" variant="body2">
                            Upload your first content to get started
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAssets.map((asset) => (
                      <React.Fragment key={asset.id}>
                        <TableRow 
                          sx={{ 
                            '&:hover': { bgcolor: 'action.hover' },
                            bgcolor: editingAssetId === asset.id ? 'action.selected' : 'inherit'
                          }}
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={2}>
                              {asset.thumbnailUrl ? (
                                <img 
                                  src={asset.thumbnailUrl} 
                                  alt={asset.title}
                                  style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: 2,
                                    bgcolor: 'grey.200',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  {asset.type === 'AUDIO' ? <AudioIcon /> : 
                                   asset.type === 'VIDEO' ? <VideoIcon /> :
                                   asset.type === 'PDF' ? <PdfIcon /> : <WordIcon />}
                                </Box>
                              )}
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600}>{asset.title}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {asset.description?.substring(0, 50)}{asset.description?.length > 50 ? '...' : ''}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              <Chip 
                                label={asset.type} 
                                size="small" 
                                color={asset.type === 'AUDIO' ? 'primary' : asset.type === 'VIDEO' ? 'secondary' : 'default'}
                                icon={asset.type === 'AUDIO' ? <AudioIcon /> : 
                                      asset.type === 'VIDEO' ? <VideoIcon /> :
                                      asset.type === 'PDF' ? <PdfIcon /> : <WordIcon />}
                              />
                              {asset.bundleOnly && (
                                <Chip label="Bundle Only" size="small" color="warning" variant="outlined" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                              ${asset.price?.toFixed(2) || '0.00'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {asset.visibility}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={asset.categoryName || 'None'} 
                              size="small" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {asset.tags && asset.tags.length > 0 ? (
                                asset.tags.slice(0, 2).map(tag => (
                                  <Chip 
                                    key={tag.id} 
                                    label={tag.name} 
                                    size="small" 
                                    style={{ backgroundColor: tag.color || '#e0e0e0', fontSize: '0.7rem' }}
                                  />
                                ))
                              ) : (
                                <Typography variant="caption" color="textSecondary">None</Typography>
                              )}
                              {asset.tags && asset.tags.length > 2 && (
                                <Chip label={`+${asset.tags.length - 2}`} size="small" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {asset.bundles && asset.bundles.length > 0 ? (
                                <>
                                  {asset.bundles.slice(0, 2).map(bundle => (
                                    <Chip
                                      key={bundle.id}
                                      label={bundle.name}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  ))}
                                  {asset.bundles.length > 2 && (
                                    <Tooltip title={asset.bundles.slice(2).map(b => b.name).join(', ')}>
                                      <Chip label={`+${asset.bundles.length - 2}`} size="small" />
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                <Typography variant="caption" color="textSecondary">None</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {getEligibleTeamsDisplay(asset.eligibleTeams)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={asset.approved
                                ? `Approved${asset.approvedBy ? ` by ${asset.approvedBy}` : ''} — visible to members`
                                : 'Pending approval — hidden from every member, including buyers'}
                            >
                              <Chip
                                label={asset.approved ? 'Approved' : 'Pending'}
                                color={asset.approved ? 'success' : 'default'}
                                size="small"
                                variant={asset.approved ? 'filled' : 'outlined'}
                                sx={{ mb: 0.5, display: 'flex', width: 'fit-content' }}
                              />
                            </Tooltip>
                            <Tooltip title={asset.status}>
                              <Chip
                                label={asset.status}
                                color={getStatusColor(asset.status)}
                                size="small"
                                icon={asset.status === 'READY' ? <CheckIcon /> : undefined}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {asset.durationSeconds ? 
                                `${Math.floor(asset.durationSeconds / 60)}:${(asset.durationSeconds % 60).toString().padStart(2, '0')}` 
                                : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {asset.releaseDate || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {['PROCESSING', 'FAILED', 'UPLOADING'].includes(asset.status) && (
                              <Tooltip
                                title={
                                  asset.status === 'FAILED'
                                    ? 'Retry transcoding'
                                    : asset.status === 'UPLOADING'
                                    ? 'Resume orphaned upload'
                                    : 'Resume stuck transcoding'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    disabled={resumingAssetIds.has(asset.id)}
                                    onClick={() => handleResumeAsset(asset)}
                                  >
                                    {resumingAssetIds.has(asset.id) ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <ReplayIcon />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title={asset.approved ? 'Withdraw from members' : 'Approve for members'}>
                              <IconButton
                                size="small"
                                color={asset.approved ? 'default' : 'success'}
                                onClick={() => handleToggleApproval(asset)}
                              >
                                {asset.approved ? <WithdrawIcon /> : <ApproveIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="File versions — replace the file or restore an earlier one">
                              <IconButton
                                size="small"
                                onClick={() => setVersionsAsset(asset)}
                              >
                                <VersionsIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEdit(asset)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(asset.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        
                        {/* Inline Edit Form */}
                        {editingAssetId === asset.id && editForm && (
                          <TableRow>
                            <TableCell colSpan={11} sx={{ bgcolor: 'action.hover', p: 3 }}>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                  Edit Content
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      fullWidth
                                      label="Title"
                                      required
                                      value={editForm.title || ''}
                                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                      <InputLabel>Visibility</InputLabel>
                                      <Select
                                        value={editForm.visibility || 'PAID'}
                                        onChange={(e) => setEditForm({ ...editForm, visibility: e.target.value })}
                                        label="Visibility"
                                      >
                                        <MenuItem value="PUBLIC">Public (Free)</MenuItem>
                                        <MenuItem value="PAID">Paid</MenuItem>
                                        <MenuItem value="SUBSCRIBERS_ONLY">Subscribers Only</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      label="Description"
                                      multiline
                                      rows={2}
                                      value={editForm.description || ''}
                                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      fullWidth
                                      label="Price"
                                      type="number"
                                      value={editForm.price || ''}
                                      onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                                      disabled={editForm.visibility === 'PUBLIC'}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <MoneyIcon />
                                          </InputAdornment>
                                        )
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <FormControl fullWidth sx={{ minWidth: 150 }}>
                                      <InputLabel>Category</InputLabel>
                                      <Select
                                        value={editForm.categoryId || ''}
                                        onChange={(e) => setEditForm({ ...editForm, categoryId: e.target.value })}
                                        label="Category"
                                        displayEmpty
                                      >
                                        <MenuItem value="">
                                          <em>None</em>
                                        </MenuItem>
                                        {categories.map(cat => (
                                          <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Autocomplete
                                      multiple
                                      options={bundles}
                                      getOptionLabel={(b) => b.title}
                                      value={bundles.filter(b => (editForm.bundleIds || []).includes(b.id))}
                                      onChange={(e, newValue) => {
                                        setEditForm({ ...editForm, bundleIds: newValue.map(b => b.id) });
                                      }}
                                      renderTags={(value, getTagProps) =>
                                        value.map((opt, i) => (
                                          <Chip
                                            key={opt.id}
                                            label={opt.title}
                                            size="small"
                                            {...getTagProps({ index: i })}
                                          />
                                        ))
                                      }
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Bundles"
                                          placeholder="Select bundles"
                                          sx={{ minWidth: 150 }}
                                        />
                                      )}
                                      sx={{ minWidth: 150 }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      fullWidth
                                      label="Release Date"
                                      type="date"
                                      value={editForm.releaseDate || ''}
                                      onChange={(e) => setEditForm({ ...editForm, releaseDate: e.target.value })}
                                      InputLabelProps={{ shrink: true }}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <CalendarIcon />
                                          </InputAdornment>
                                        )
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                        Thumbnail Image
                                      </Typography>
                                      <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<ImageIcon />}
                                        disabled={thumbnailUploading}
                                        fullWidth
                                        sx={{ mb: 1, justifyContent: 'flex-start', textTransform: 'none' }}
                                      >
                                        {editThumbnail ? editThumbnail.name : 'Choose File'}
                                        <input
                                          type="file"
                                          hidden
                                          accept="image/*"
                                          onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                              setEditThumbnail(file);
                                              setEditForm(prev => ({ ...prev, thumbnailUrl: '' }));
                                            }
                                          }}
                                        />
                                      </Button>
                                      {editThumbnail && (
                                        <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
                                          <img
                                            src={URL.createObjectURL(editThumbnail)}
                                            alt="Thumbnail preview"
                                            style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 8, objectFit: 'cover' }}
                                          />
                                          <IconButton
                                            size="small"
                                            onClick={() => setEditThumbnail(null)}
                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'error.light', color: 'white' } }}
                                          >
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      )}
                                      {!editThumbnail && editForm.thumbnailUrl && (
                                        <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
                                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                            Current thumbnail:
                                          </Typography>
                                          <img
                                            src={editForm.thumbnailUrl}
                                            alt="Current thumbnail"
                                            style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 8, objectFit: 'cover' }}
                                          />
                                        </Box>
                                      )}
                                      {!editThumbnail && (
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label="Or paste URL"
                                          value={editForm.thumbnailUrl || ''}
                                          onChange={(e) => setEditForm({ ...editForm, thumbnailUrl: e.target.value })}
                                          placeholder="https://example.com/thumbnail.jpg"
                                          sx={{ mt: 1 }}
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                <ImageIcon />
                                              </InputAdornment>
                                            )
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Autocomplete
                                      multiple
                                      options={tags}
                                      getOptionLabel={(option) => option.name}
                                      value={tags.filter(t => editForm.tagIds?.includes(t.id)) || []}
                                      onChange={(e, newValue) => {
                                        setEditForm({ ...editForm, tagIds: newValue.map(v => v.id) });
                                      }}
                                      renderInput={(params) => (
                                        <TextField {...params} label="Tags" placeholder="Select tags" />
                                      )}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={editForm.bundleOnly || false}
                                          onChange={(e) => setEditForm({ ...editForm, bundleOnly: e.target.checked })}
                                        />
                                      }
                                      label="Bundle Only"
                                    />
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -1 }}>
                                      If checked, this content will only be available as part of a bundle and won't appear in the public catalog
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                      Eligible Teams
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            value="all"
                                            checked={editForm.eligibleTeams.includes('all')}
                                            onChange={(e) => handleTeamSelection(e, 'edit')}
                                          />
                                        }
                                        label="All Teams"
                                      />
                                      {teams.map(team => (
                                        <FormControlLabel
                                          key={team.id || team.name}
                                          control={
                                            <Checkbox
                                              value={team.name}
                                              checked={editForm.eligibleTeams.includes(team.name)}
                                              onChange={(e) => handleTeamSelection(e, 'edit')}
                                              disabled={editForm.eligibleTeams.includes('all')}
                                            />
                                          }
                                          label={team.name}
                                        />
                                      ))}
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Box display="flex" gap={2} justifyContent="flex-end">
                                      <Button
                                        variant="outlined"
                                        startIcon={<CancelIcon />}
                                        onClick={handleCancelEdit}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SaveIcon />}
                                        onClick={handleUpdate}
                                      >
                                        Save Changes
                                      </Button>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </CardContent>
      </Card>

      {/* Bulk Resume Dialog */}
      <Dialog
        open={bulkResumeOpen}
        onClose={closeBulkResumeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resume Stuck Assets</DialogTitle>
        <DialogContent dividers>
          {!bulkResumeResult && (
            <Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Re-publish transcode jobs for assets that look stuck. PROCESSING and
                UPLOADING are filtered by the threshold; FAILED assets are always retried.
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="Threshold (minutes)"
                value={bulkResumeForm.thresholdMinutes}
                onChange={(e) =>
                  setBulkResumeForm({
                    ...bulkResumeForm,
                    thresholdMinutes: e.target.value
                  })
                }
                inputProps={{ min: 1 }}
                sx={{ mb: 2 }}
              />
              <FormLabel component="legend" sx={{ mb: 1 }}>
                Statuses to resume
              </FormLabel>
              <FormGroup>
                {['PROCESSING', 'FAILED', 'UPLOADING'].map((s) => (
                  <FormControlLabel
                    key={s}
                    control={
                      <Checkbox
                        checked={Boolean(bulkResumeForm.statuses[s])}
                        onChange={(e) =>
                          setBulkResumeForm({
                            ...bulkResumeForm,
                            statuses: {
                              ...bulkResumeForm.statuses,
                              [s]: e.target.checked
                            }
                          })
                        }
                      />
                    }
                    label={
                      s === 'UPLOADING'
                        ? 'UPLOADING (orphaned uploads with file already in S3)'
                        : s === 'PROCESSING'
                          ? 'PROCESSING (also replacement files stuck transcoding)'
                          : s
                    }
                  />
                ))}
              </FormGroup>
            </Box>
          )}

          {bulkResumeResult && (
            <Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                <Chip
                  label={`Considered: ${bulkResumeResult.requestedCount ?? 0}`}
                  size="small"
                />
                <Chip
                  label={`Requeued: ${bulkResumeResult.requeuedCount ?? 0}`}
                  color="success"
                  size="small"
                />
                <Chip
                  label={`Marked Ready: ${bulkResumeResult.markedReadyCount ?? 0}`}
                  color="success"
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Skipped: ${bulkResumeResult.skippedCount ?? 0}`}
                  color="default"
                  size="small"
                />
                <Chip
                  label={`Failed: ${bulkResumeResult.failedCount ?? 0}`}
                  color={(bulkResumeResult.failedCount ?? 0) > 0 ? 'error' : 'default'}
                  size="small"
                />
              </Stack>

              {Array.isArray(bulkResumeResult.items) && bulkResumeResult.items.length > 0 && (
                <Box>
                  <Button
                    size="small"
                    onClick={() => setBulkResumeShowItems((v) => !v)}
                    endIcon={bulkResumeShowItems ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  >
                    {bulkResumeShowItems ? 'Hide details' : `Show ${bulkResumeResult.items.length} details`}
                  </Button>
                  <Collapse in={bulkResumeShowItems}>
                    <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {bulkResumeResult.items.map((item) => (
                        <ListItem key={`${item.assetId}-${item.versionNo ?? 'asset'}`} divider>
                          <ListItemText
                            primary={
                              <Box display="flex" gap={1} alignItems="center">
                                <Chip
                                  label={item.action}
                                  size="small"
                                  color={
                                    item.action === 'REQUEUED' || item.action === 'MARKED_READY'
                                      ? 'success'
                                      : item.action === 'FAILED'
                                      ? 'error'
                                      : 'default'
                                  }
                                />
                                <Chip
                                  label={`was ${item.previousStatus}`}
                                  size="small"
                                  variant="outlined"
                                />
                                {item.versionNo != null && (
                                  // A replacement file, not the asset: the live file kept playing.
                                  <Chip label={`replacement v${item.versionNo}`} size="small" color="info" variant="outlined" />
                                )}
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {item.assetId}
                                </Typography>
                              </Box>
                            }
                            secondary={item.reason}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkResumeDialog} disabled={bulkResumeRunning}>
            {bulkResumeResult ? 'Close' : 'Cancel'}
          </Button>
          {!bulkResumeResult && (
            <Button
              variant="contained"
              color="warning"
              startIcon={
                bulkResumeRunning ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RestartAltIcon />
                )
              }
              disabled={bulkResumeRunning}
              onClick={handleResumeStuck}
            >
              {bulkResumeRunning ? 'Resuming…' : 'Resume'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* File versions: replace the file, or put an earlier one back. Refetching the
          asset list on change keeps the row's status and duration in step with the
          version that is now live. */}
      <AssetVersionsDialog
        open={Boolean(versionsAsset)}
        asset={versionsAsset}
        onClose={() => setVersionsAsset(null)}
        onChanged={fetchAssets}
        showNotification={showNotification}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContentManagementPage;
